import React, { useState, useContext, useEffect, Fragment, useRef } from 'react';
import PropTypes from 'prop-types';
import styles from './style.module.css';
import MobileContext from 'context/MobileContext';

const FitnessLevelStep = ({ onNext, onBack, stepData }) => {
  const isMobile = useContext(MobileContext);

  // Extract dynamic configurations from stepData.sliders and sort by displayOrder
  const sliders = stepData?.sliders || [];

  // Sort sliders by displayOrder (same logic as WorkoutSelectionStep)
  const sortedSliders = sliders.sort(
    (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
  );

  // Create dynamic state for each slider based on their hubspotProperty
  const [sliderValues, setSliderValues] = useState({});

  // Combined effect: Initialize slider values and handle pre-fill in one effect
  useEffect(() => {
    const initialValues = {};

    // First, initialize all sliders to 0 (default/unselected state)
    sortedSliders.forEach(slider => {
      if (slider.hubspotProperty) {
        initialValues[slider.hubspotProperty] = 0; // Default value 0 (no selection)
      }
    });

    // Then, if we have pre-existing answers, fill them in
    if (stepData?.hasAnswered && stepData?.sliders) {
      stepData.sliders.forEach(slider => {
        const hubspotProperty = slider.hubspotProperty;
        if (!hubspotProperty) return;
        if (
          slider.userResponse &&
          Array.isArray(slider.userResponse) &&
          slider.userResponse.length > 0
        ) {
          // userResponse is an array, get the first value
          const responseValue = slider.userResponse[0];

          // Check if the response is the hubspotSkipValue (meaning user skipped)
          const isSkippedValue =
            slider.hubspotSkipValue && responseValue === slider.hubspotSkipValue;

          // Only use the value if it's not the skip value
          if (responseValue && !isSkippedValue) {
            // Find the step number that matches the userResponse value
            const matchingStep = slider.sliderSteps?.find(
              step => step.value === responseValue
            );
            if (matchingStep) {
              initialValues[hubspotProperty] = matchingStep.stepNumber;
            }
          }
          // If userResponse is hubspotSkipValue or null, keep slider at 0 (initial position)
        }
      });
    }

    setSliderValues(initialValues);
  }, [stepData?.hasAnswered, stepData?.sliders, sortedSliders.length]);

  // Helper function to get slider config from any slider
  const getSliderConfig = slider => {
    return {
      title: slider?.title,
      options:
        slider?.sliderSteps?.map(step => ({
          value: step.stepNumber,
          label: step.label,
          apiValue: step.value, // Store the API value for submission
        })) || [],
    };
  };

  // Helper function to calculate mobile label positions (same as dot positions)
  const calculateMobileLabelPosition = (index, totalOptions) => {
    // Use the same logic as calculateMobileDotPosition for perfect alignment
    return calculateMobileDotPosition(index, totalOptions);
  };

  // Helper function to calculate mobile dot positions within track (adaptive spacing)
  const calculateMobileDotPosition = (index, totalOptions) => {
    if (totalOptions === 1) return '50%';

    // Check if screen width is mobile (769px and below)
    const isMobileWidth = window.innerWidth <= 769;

    if (isMobileWidth) {
      // Mobile spacing with minimum gap from initial thumb position
      const minStartGap = 12; // Minimum 12% gap from initial thumb (0%) to first marker
      const endMargin = 5; // Fixed 5% margin at end
      let startPosition;

      if (totalOptions === 2) {
        // For 2 options: balanced spacing
        startPosition = 20;
      } else if (totalOptions === 3) {
        // For 3 options: moderate spacing
        startPosition = 15;
      } else {
        // For 4+ options: ensure minimum gap from initial thumb
        startPosition = minStartGap; // Always at least 12% from initial thumb
      }

      const endPosition = 100 - endMargin; // Always 5% margin at end (95%)
      const availableSpace = endPosition - startPosition;
      const step = availableSpace / (totalOptions - 1);
      const percentage = startPosition + index * step;

      return `${percentage}%`;
    } else {
      // Tablet/larger mobile (>769px) - use desktop adaptive spacing
      // Adaptive margin system based on number of options
      let startMargin, endMargin;

      if (totalOptions === 2) {
        startMargin = 25;
        endMargin = 25;
      } else if (totalOptions === 3) {
        startMargin = 20;
        endMargin = 20;
      } else if (totalOptions <= 5) {
        startMargin = 15;
        endMargin = 15;
      } else {
        startMargin = 10;
        endMargin = 10;
      }

      const availableSpace = 100 - startMargin - endMargin;
      const step = availableSpace / (totalOptions - 1);
      const percentage = startMargin + index * step;
      return `${percentage}%`;
    }
  };

  // Helper function to update slider value
  const updateSliderValue = (hubspotProperty, newValue) => {
    setSliderValues(prev => ({
      ...prev,
      [hubspotProperty]: newValue,
    }));
  };

  const handleNextClick = () => {
    if (onNext) {
      // Build the submission data dynamically for all sliders
      const submissionData = {};

      sortedSliders.forEach(slider => {
        const hubspotProperty = slider.hubspotProperty;
        if (!hubspotProperty) return;

        const sliderValue = sliderValues[hubspotProperty] || 0;

        if (sliderValue > 0) {
          // User has selected a value - send the actual API value
          const selectedOption = slider.sliderSteps?.find(
            step => step.stepNumber === sliderValue
          );
          submissionData[hubspotProperty] = selectedOption?.value;
        } else {
          // User hasn't moved the slider - use hubspotSkipValue
          submissionData[hubspotProperty] = slider.hubspotSkipValue;
        }
      });

      onNext(submissionData);
    }
  };

  const handleSkipClick = () => {
    if (onNext) {
      // Clear current slider selections when skipping
      const resetValues = {};
      sortedSliders.forEach(slider => {
        if (slider.hubspotProperty) {
          resetValues[slider.hubspotProperty] = 0;
        }
      });
      setSliderValues(resetValues);

      const skipData = {};

      // For skip, use each slider's hubspotSkipValue if available
      sortedSliders.forEach(slider => {
        const hubspotProperty = slider.hubspotProperty;
        if (hubspotProperty) {
          // Use slider's specific hubspotSkipValue
          skipData[hubspotProperty] = slider.hubspotSkipValue;
        }
      });

      onNext(skipData);
    }
  };

  // Validation function based on API minInput/maxInput
  const isNextButtonEnabled = () => {
    const minInput = stepData?.validations?.minInput;
    const maxInput = stepData?.validations?.maxInput;

    // Count how many sliders have been set (value > 0)
    let selectionCount = 0;
    Object.values(sliderValues).forEach(value => {
      if (value > 0) selectionCount++;
    });

    // Check minimum requirement
    if (minInput > 0 && selectionCount < minInput) {
      return false;
    }

    // Check maximum requirement
    if (selectionCount > maxInput) {
      return false;
    }

    return true;
  };

  // Helper function to determine if a slider thumb should be active
  const isThumbActive = value => value > 0;

  // Helper function to get slider value and update function
  const getSliderValueAndSetter = hubspotProperty => {
    return {
      value: sliderValues[hubspotProperty] || 0,
      setValue: newValue => updateSliderValue(hubspotProperty, newValue),
    };
  };

  // Helper function to get slider CSS class based on displayOrder
  const getSliderClass = (slider, index) => {
    // Use displayOrder/index for truly dynamic class assignment
    // This ensures styling is consistent regardless of hubspotProperty names

    // Map displayOrder to existing CSS classes for backward compatibility
    const displayOrder = slider.displayOrder || index + 1;

    if (displayOrder === 1) {
      return styles.fitnessLevelSlider; // First slider (regardless of content)
    } else if (displayOrder === 2) {
      return styles.weeklyCheckInSlider; // Second slider (regardless of content)
    }

    // For future sliders beyond 2, could add generic classes or return empty
    return '';
  };

  // Custom Desktop Slider Component - maintains original UI with custom label positioning
  const CustomDesktopSlider = ({
    slider,
    title,
    options,
    value,
    onValueChange,
    sliderClass,
  }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [sliderRef, setSliderRef] = useState(null);
    const [dragPosition, setDragPosition] = useState(null);
    const dragPositionRef = useRef(null);

    // Add global event listeners for drag functionality
    useEffect(() => {
      if (isDragging && sliderRef) {
        const handleGlobalMouseMove = e => {
          const rect = sliderRef.getBoundingClientRect();
          const x = (e.clientX || (e.touches && e.touches[0].clientX)) - rect.left;
          const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
          // During drag, just update position without snapping
          dragPositionRef.current = percentage;
          setDragPosition(percentage);
        };
        const handleGlobalMouseUp = () => {
          // Snap to closest position when drag ends
          const finalPosition = dragPositionRef.current;

          if (finalPosition !== null) {
            handleSliderUpdate(finalPosition);
          }
          dragPositionRef.current = null;
          setDragPosition(null);
          setIsDragging(false);
        };
        const handleGlobalTouchMove = e => {
          e.preventDefault();
          const rect = sliderRef.getBoundingClientRect();
          const x = (e.clientX || (e.touches && e.touches[0].clientX)) - rect.left;
          const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
          // During drag, just update position without snapping
          dragPositionRef.current = percentage;
          setDragPosition(percentage);
        };
        const handleGlobalTouchEnd = () => {
          // Snap to closest position when drag ends
          const finalPosition = dragPositionRef.current;

          if (finalPosition !== null) {
            handleSliderUpdate(finalPosition);
          }
          dragPositionRef.current = null;
          setDragPosition(null);
          setIsDragging(false);
        };

        document.addEventListener('mousemove', handleGlobalMouseMove);
        document.addEventListener('mouseup', handleGlobalMouseUp);
        document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
        document.addEventListener('touchend', handleGlobalTouchEnd);

        return () => {
          document.removeEventListener('mousemove', handleGlobalMouseMove);
          document.removeEventListener('mouseup', handleGlobalMouseUp);
          document.removeEventListener('touchmove', handleGlobalTouchMove);
          document.removeEventListener('touchend', handleGlobalTouchEnd);
        };
      }
    }, [isDragging, sliderRef]);

    // Calculate positions for marks and labels with adaptive margins
    const getMarkPosition = index => {
      if (options.length === 1) return 50;

      // Adaptive margin system based on number of options
      let startMargin, endMargin;

      if (options.length === 2) {
        // For 2 options: smaller margins for better visual balance
        startMargin = 25; // 25% margin at start
        endMargin = 25; // 25% margin at end
        // Results in 50% gap between the two options (25% to 75%)
      } else if (options.length === 3) {
        // For 3 options: moderate margins
        startMargin = 20; // 20% margin at start
        endMargin = 20; // 20% margin at end
        // Results in 30% spacing between options (20%, 50%, 80%)
      } else if (options.length <= 5) {
        // For 4-5 options: standard margins
        startMargin = 15; // 15% margin at start
        endMargin = 15; // 15% margin at end
      } else {
        // For 6+ options: tighter margins to fit more options
        startMargin = 10; // 10% margin at start
        endMargin = 10; // 10% margin at end
      }

      const availableSpace = 100 - startMargin - endMargin;
      const step = availableSpace / (options.length - 1);
      const percentage = startMargin + index * step;

      return percentage;
    };

    // Handle slider update with simple percentage-based snapping
    const handleSliderUpdate = percentage => {
      // Create array of marker positions including initial position (0%)
      const markPositions = [0, ...options.map((_, index) => getMarkPosition(index))];
      const values = [0, ...options.map(item => item.value)];

      // Find closest marker position by percentage
      let closestIndex = 0;
      let minDistance = Math.abs(percentage - markPositions[0]);

      for (let i = 1; i < markPositions.length; i++) {
        const distance = Math.abs(percentage - markPositions[i]);
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = i;
        }
      }

      // Set the value based on closest position
      onValueChange(values[closestIndex]);
    };

    // Handle initial track interaction
    const handleTrackInteraction = e => {
      const sliderElement = e.currentTarget;
      const rect = sliderElement.getBoundingClientRect();
      const x = (e.clientX || (e.touches && e.touches[0].clientX)) - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      // Set drag position for initial click, don't snap immediately
      dragPositionRef.current = percentage;
      setDragPosition(percentage);
    };

    // Get thumb position based on selected value or drag position
    const getThumbPosition = () => {
      // During drag, show the live drag position
      if (isDragging && dragPosition !== null) {
        return dragPosition;
      }

      // When not dragging, show the snapped position
      if (value === 0) {
        return 0; // Initial position at start
      }
      const selectedIndex = options.findIndex(opt => opt.value === value);
      if (selectedIndex === -1) {
        return 0;
      }
      const position = getMarkPosition(selectedIndex);
      return position;
    };

    return (
      <div className={`${styles.sliderSection} ${sliderClass}`}>
        <h3 className={styles.sectionTitle}>{title}</h3>

        {/* Use original slider container structure */}
        <div className={styles.sliderContainer}>
          <div
            ref={el => setSliderRef(el)}
            className={styles.customSlider}
            onMouseDown={e => {
              setIsDragging(true);
              handleTrackInteraction(e);
            }}
            onTouchStart={e => {
              setIsDragging(true);
              handleTrackInteraction(e);
            }}
          >
            {/* Grey track (same as original) */}
            <div
              className={styles.customTrack}
              style={{
                width: '100%',
                left: 0,
                right: 0,
              }}
            ></div>

            {/* Marks positioned dynamically */}
            {options.map((option, index) => (
              <div
                key={`mark-${option.value}`}
                className={styles.customMark}
                style={{
                  position: 'absolute',
                  left: `${getMarkPosition(index)}%`,
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
                onClick={() => onValueChange(option.value)}
              />
            ))}

            {/* Thumb (same styling as original) */}
            <div
              className={`${styles.customThumb} ${
                isThumbActive(value) ? styles.active : ''
              }`}
              style={{
                position: 'absolute',
                left: `${getThumbPosition()}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
              }}
              onMouseDown={handleTrackInteraction}
              onTouchStart={handleTrackInteraction}
            />
          </div>
        </div>

        {/* Labels with original container structure but custom positioning */}
        <div className={styles.sliderLabels}>
          {options.map((option, index) => (
            <div
              key={`label-${option.value}`}
              className={`${styles.sliderLabel} ${
                Math.round(value) === option.value ? styles.activeLabel : ''
              }`}
              style={{
                position: 'absolute',
                left: `${getMarkPosition(index)}%`,
                transform: 'translateX(-50%)',
                textAlign: 'center',
              }}
              onClick={() => onValueChange(option.value)}
            >
              {option.label}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Mobile-specific slider component
  const MobileSlider = ({ title, options, value, onValueChange, sliderClass }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [dragPosition, setDragPosition] = useState(null);
    const dragPositionRef = useRef(null);

    const handleDotClick = optionValue => {
      onValueChange(optionValue);
    };

    // Calculate thumb position based on selected option or drag position
    const getThumbPosition = () => {
      // During drag, show the live drag position
      if (isDragging && dragPosition !== null) {
        return `${dragPosition}%`;
      }

      // When not dragging, show the snapped position
      // If no value selected, position at initial position (always 0%)
      if (value === 0) {
        return '0%';
      }

      const selectedIndex = options.findIndex(opt => opt.value === value);
      if (selectedIndex === -1) {
        return '0%';
      }

      // Position thumb at the selected dot
      const position = calculateMobileDotPosition(selectedIndex, options.length);
      return position;
    };

    // Handle track click and drag
    const handleTrackInteraction = e => {
      const trackElement = e.currentTarget.querySelector(`.${styles.mobileSliderTrack}`);
      if (!trackElement) return;

      const rect = trackElement.getBoundingClientRect();
      const x = (e.clientX || (e.touches && e.touches[0].clientX)) - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));

      if (isDragging) {
        // During drag, just update position without snapping
        dragPositionRef.current = percentage;
        setDragPosition(percentage);
      } else {
        // On initial click (not dragging), snap immediately
        snapToClosest(percentage);
      }
    };

    // Snap to closest position
    const snapToClosest = percentage => {
      // Create array of marker positions including initial position (0%) and all option positions
      const markPositions = [0]; // Initial position at 0%
      for (let i = 0; i < options.length; i++) {
        const position = parseFloat(calculateMobileDotPosition(i, options.length));
        markPositions.push(position);
      }
      const values = [0, ...options.map(item => item.value)];

      // Find closest marker position by percentage
      let closestIndex = 0;
      let minDistance = Math.abs(percentage - markPositions[0]);

      for (let i = 1; i < markPositions.length; i++) {
        const distance = Math.abs(percentage - markPositions[i]);
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = i;
        }
      }

      onValueChange(values[closestIndex]);
    };

    // Mouse events
    const handleMouseDown = e => {
      setIsDragging(true);
      handleTrackInteraction(e);
    };

    const handleMouseMove = e => {
      if (!isDragging) return;
      handleTrackInteraction(e);
    };

    const handleMouseUp = () => {
      // Snap to closest position when drag ends
      const finalPosition = dragPositionRef.current;
      if (finalPosition !== null) {
        snapToClosest(finalPosition);
      }
      dragPositionRef.current = null;
      setDragPosition(null);
      setIsDragging(false);
    };

    // Touch events
    const handleTouchStart = e => {
      setIsDragging(true);
      handleTrackInteraction(e);
    };

    const handleTouchMove = e => {
      if (!isDragging) return;
      e.preventDefault(); // Prevent scrolling
      handleTrackInteraction(e);
    };

    const handleTouchEnd = () => {
      // Snap to closest position when drag ends
      const finalPosition = dragPositionRef.current;
      if (finalPosition !== null) {
        snapToClosest(finalPosition);
      }
      dragPositionRef.current = null;
      setDragPosition(null);
      setIsDragging(false);
    };

    // Add global event listeners for mouse up/move when dragging
    useEffect(() => {
      if (isDragging) {
        const handleGlobalMouseMove = e => handleMouseMove(e);
        const handleGlobalMouseUp = () => handleMouseUp();
        const handleGlobalTouchMove = e => handleTouchMove(e);
        const handleGlobalTouchEnd = () => handleTouchEnd();

        document.addEventListener('mousemove', handleGlobalMouseMove);
        document.addEventListener('mouseup', handleGlobalMouseUp);
        document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
        document.addEventListener('touchend', handleGlobalTouchEnd);

        return () => {
          document.removeEventListener('mousemove', handleGlobalMouseMove);
          document.removeEventListener('mouseup', handleGlobalMouseUp);
          document.removeEventListener('touchmove', handleGlobalTouchMove);
          document.removeEventListener('touchend', handleGlobalTouchEnd);
        };
      }
    }, [isDragging, options, onValueChange]);

    return (
      <div className={`${styles.sliderSection} ${sliderClass}`}>
        <h3 className={styles.sectionTitle}>{title}</h3>
        <div
          className={styles.mobileSliderContainer}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          {/* Custom mobile slider track */}
          <div
            className={styles.mobileSliderTrack}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {/* Slider dots */}
            {options.map((option, index) => (
              <div
                key={option.value}
                className={`${styles.mobileDot} ${
                  value === option.value ? styles.activeDot : ''
                }`}
                style={{
                  position: 'absolute',
                  left: calculateMobileDotPosition(index, options.length),
                  transform: 'translateX(-50%)',
                }}
                onClick={() => handleDotClick(option.value)}
              />
            ))}

            {/* Mobile thumb - always show, even at initial position */}
            <div
              className={`${styles.mobileThumb} ${value === 0 ? styles.initial : ''} ${
                isDragging ? styles.dragging : ''
              }`}
              style={{
                left: getThumbPosition(),
              }}
              onMouseDown={e => {
                e.stopPropagation();
                handleMouseDown(e);
              }}
              onTouchStart={e => {
                e.stopPropagation();
                handleTouchStart(e);
              }}
            />
          </div>

          {/* Labels */}
          <div className={styles.mobileSliderLabels}>
            {options.map((option, index) => (
              <div
                key={`mobile-label-${option.value}`}
                className={`${styles.mobileSliderLabel} ${
                  value === option.value ? styles.activeLabel : ''
                }`}
                style={{
                  position: 'absolute',
                  left: calculateMobileLabelPosition(index, options.length),
                  transform: 'translateX(-50%)',
                  textAlign: 'center',
                }}
                onClick={() => handleDotClick(option.value)}
              >
                {option.label}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerContainer}>
        <h2 className={styles.header}>{stepData?.title}</h2>
        {stepData?.subtitle && <p className={styles.subHeader}>{stepData.subtitle}</p>}
      </div>

      <div className={styles.contentContainer}>
        {isMobile ? (
          // Mobile UI - Dynamic implementation based on sorted sliders
          <>
            {sortedSliders.map((slider, index) => {
              const hubspotProperty = slider.hubspotProperty;
              const { value, setValue } = getSliderValueAndSetter(hubspotProperty);
              const config = getSliderConfig(slider);
              const sliderClass = getSliderClass(slider, index);

              return (
                <Fragment key={hubspotProperty || index}>
                  <MobileSlider
                    title={config.title}
                    options={config.options}
                    value={value}
                    onValueChange={setValue}
                    sliderClass={sliderClass}
                  />
                  {index < sortedSliders.length - 1 && (
                    <div className={styles.divider}></div>
                  )}
                </Fragment>
              );
            })}
          </>
        ) : (
          // Desktop UI - Dynamic implementation based on sorted sliders
          <>
            {sortedSliders.map((slider, index) => {
              const hubspotProperty = slider.hubspotProperty;
              const { value, setValue } = getSliderValueAndSetter(hubspotProperty);
              const config = getSliderConfig(slider);
              const sliderClass = getSliderClass(slider, index);

              return (
                <Fragment key={hubspotProperty || index}>
                  <CustomDesktopSlider
                    slider={slider}
                    title={config.title}
                    options={config.options}
                    value={value}
                    onValueChange={setValue}
                    sliderClass={sliderClass}
                  />
                  {index < sortedSliders.length - 1 && (
                    <div className={styles.divider}></div>
                  )}
                </Fragment>
              );
            })}
          </>
        )}
      </div>

      <div className={styles.buttonsContainer}>
        <div className={styles.buttonGroup}>
          <button type="button" className={styles.skipButton} onClick={handleSkipClick}>
            Skip
          </button>
          <button
            type="button"
            className={styles.nextButton}
            disabled={!isNextButtonEnabled()}
            onClick={handleNextClick}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

FitnessLevelStep.propTypes = {
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  stepData: PropTypes.object,
};

FitnessLevelStep.defaultProps = {
  onBack: () => {},
};

export default FitnessLevelStep;
